import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  InputAdornment,
  Typography,
} from '@mui/material';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {AxiosError} from 'axios';
import {t} from 'i18next';
import {noop} from 'lodash';
import {ReactElement, useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {GridExpandMoreIcon} from '@mui/x-data-grid';
import {
  addNewGroup,
  deleteSavedSearchGroupTemplate,
  deleteSavedSearchTemplateById,
  getSavedSearchesTemplateGroupsRoot,
  getTemplateGroupDetails,
  getTemplateSavedSearchesDetails,
  updateSavedSearchGroupTemplate,
  updateSavedSearchTemplate,
  updateTemplateSavedSearchesDetails,
} from '../../../../api/savedSearchTemplateService';
import {ReactComponent as InfoIcon} from '../../../../assests/svg/info-outlined-red.svg';
import {
  ERROR_STATUS_CODE,
  MAX_CHAR,
  MIN_SEARCH_CHAR,
  TOAST_TIMEOUT,
} from '../../../../constants/constants';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {
  IGlobalSelectedItem,
  setSavedSearchLastRun,
} from '../../../../redux/slices/ecaAdhocSearchSlice';
import {RootState} from '../../../../redux/store';
import {
  createQueryUsingScopeObj,
  extractSavedSearchId,
  findTreeNodeById,
  getExpandableIds,
  getLabelFromScopeQuery,
  getSavedSearchResFromEntity,
  getTreeObjectFromSavedSearch,
  checkSavedSearchLastRun,
  getUTCDateTimeForDisplay,
  isEmpty,
  isValidName,
  unwrapQueryText,
  wrapQueryBeforeSymbol,
} from '../../../../utils/utils';
import AurButton from '../../../common/aur-button/AurButton';
import AurIconButton from '../../../common/aur-button/AurIconButton';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurPopup from '../../../common/aur-popup/AurPopup';
import AurSearchBox from '../../../common/aur-search-box/AurSearchBox';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import AurLocationTreeView from '../../../eca-doc-list/eca-save-search/AurLocationTreeView';
import {ExtendedTreeItemProps} from '../../../eca-doc-list/eca-save-search/EcaSaveSearch';
import ECAFlyout from '../../../eca/ECAFlyout';
import MoveSavedSearchGroupPopup from '../../../eca/MoveSavedSearchGroupPopup';
import {RelationalGroup} from '../../index-search/RelationshipGroupPopup';
import EmptyTemplateBackground from './EmptyTemplateBackground';
import {SavedSearchRes} from '../../../eca/SavedSearchPanel';
import {AurLoadingButton} from '../../../common/aur-loading-button/AurLoadingButton';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';

export enum Flow_Type {
  STR = 'STR',
  OTHER = 'Other',
}
interface IFilterItem {
  column: string;
  operator: string;
  value: string;
  linkOperator?: string;
  items?: IFilterItem[];
}
export interface IFilterSet {
  linkOperator: string;
  items: IFilterItem[];
}

export interface SavedSearchDetails {
  name: string;
  id: string;
  useAsScope: boolean;
  createdDetail: {
    createdUserId: string;
    createdUserName: string;
    createdTime: string;
  };
  updatedDetail: {
    updatedUserId: string;
    updatedUserName: string;
    updatedTime: string;
  };
  lastRunDetail: {
    lastRunUserId: string;
    lastRunUserName: string;
    lastRunTime: string;
  };
  type: string;
  queryBuilderFlag: boolean;
  query: string;
  noScopeDtSyntaxQuery?: string;
  dtSyntaxQuery?: string;
  filterSet: IFilterSet;
  docCount: number;
  notes: string;
  fieldTemplateId: number;
  indexName: string;
  ecaSearchId: string;
  isPublicFlag: boolean;
  applyFilter: boolean;
  scope: {
    scopeType: string;
    scopeId: string;
    scopeName: string;
  };
  columnOrder: string[];
  relationalGroups?: number[];
  relationalGroupFieldList?: number[];
  sortOrder: {
    column: string;
    direction: string;
  }[];
  groupId: string;
  isDefault: boolean;
  relationalGroupList?: RelationalGroup[];
}
const initialSavedSearchDetails = {
  name: '',
  id: '',
  useAsScope: false,
  createdDetail: {
    createdUserId: '',
    createdUserName: '',
    createdTime: '',
  },
  updatedDetail: {
    updatedUserId: '',
    updatedUserName: '',
    updatedTime: '',
  },
  lastRunDetail: {
    lastRunUserId: '',
    lastRunUserName: '',
    lastRunTime: '',
  },
  type: '0',
  queryBuilderFlag: false,
  query: '',
  noScopeDtSyntaxQuery: '',
  dtSyntaxQuery: '',
  docCount: 0,
  filterSet: {
    linkOperator: '',
    items: [
      {
        column: '',
        operator: '',
        value: '',
      },
    ],
  },
  notes: '',
  fieldTemplateId: 0,
  indexName: '',
  ecaSearchId: '',
  isPublicFlag: false,
  applyFilter: false,
  scope: {
    scopeType: '',
    scopeId: '',
    scopeName: '',
  },
  columnOrder: [''],
  relationalGroups: [],
  sortOrder: [
    {
      column: '',
      direction: '',
    },
  ],
  groupId: '',
  isDefault: false,
};

interface ISavedGroupDetails {
  createdBy: string;
  createdTime: string;
  updatedBy: string;
  updatedTime: string;
}

const initialSavedGoupDetails = {
  createdBy: '',
  createdTime: '',
  updatedBy: '',
  updatedTime: '',
};

interface Props {
  selectedIndex: string;
  enableFlayout?: boolean;
  renderSelectedSearchDetail?: (element: ReactElement) => void;
  openForPickSavedSearch?: boolean; // Set it true when open this for select saved search
  enableSearch?: boolean;
  searchInput?: string;
  showNotes?: boolean;
  indexNameLabel?: string;
  onPickSavedSearch?: (
    data: {
      id: string;
      label: string;
      ecaSearchId: string;
      directory_structure_ids?: string;
    } | null
  ) => void;
  onFetchSavedSearch?: (savedSearchDetails: SavedSearchDetails) => void;
  selectedSearch?: TreeViewBaseItem<ExtendedTreeItemProps> | null;
  expandedIds?: string[];
  handleUseAsScope?: (flag: string, selectedItem: IGlobalSelectedItem) => void;
  handleViewDocs?: (flag: string, selectedItem: IGlobalSelectedItem) => void;
  folderDetailsView?: boolean;
  showActions?: boolean;
  manageSaved?: boolean;
  manageSearchRename?: boolean;
  deleteOption?: boolean;
  isDeleteSuccessful?: boolean;
  ecaAdminPermission?: boolean;
  handleSearchId?: boolean;
  drawerRef?: React.RefObject<HTMLDivElement>;
  userId?: string;
  flowType?: Flow_Type;
  clientId: string;
}

interface PopupConfig {
  title: string;
  heading: string;
  content: string;
  buttonCancel: string;
  buttonYes: string;
  showCancel: boolean;
}

const SavedSearchesTemplatePanel: React.FC<Props> = ({
  enableFlayout = true,
  renderSelectedSearchDetail,
  onPickSavedSearch,
  openForPickSavedSearch = false,
  enableSearch = true,
  searchInput,
  showNotes = true,
  indexNameLabel = t('eca.flyout.savedSearch.indexName'),
  selectedSearch = null,
  expandedIds = [''],
  folderDetailsView = false,
  showActions = false,
  manageSaved = false,
  manageSearchRename = false,
  isDeleteSuccessful = false,
  handleSearchId = false,
  onFetchSavedSearch,
  drawerRef,
  clientId,
  flowType = Flow_Type.OTHER,
}) => {
  const [isCreatingRootFolder, setIsCreatingRootFolder] = useState(false);
  const [hasTemplate, setHasTemplate] = useState<boolean>(false);
  const [treeData, setTreeData] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps>[]>();
  const [currentExpandedIds, setCurrentExpandedIds] = useState(expandedIds);
  const [saveSearchedData, setSaveSearchedData] = useState<
    TreeViewBaseItem<ExtendedTreeItemProps>[]
  >([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [showFlyout, setShowFlyout] = useState(false);
  const [savedSearchedDetails, setSavedSearchedDetails] =
    useState<SavedSearchDetails>(initialSavedSearchDetails);
  const [loadingLastDocCount, setLoadingLastDocCount] = useState<boolean>();
  const [loadingFlyout, setLoadingFlyout] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedNode, setSelectedNode] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>(selectedSearch);
  const [selectedNodeToMove, setSelectedNodeToMove] =
    useState<TreeViewBaseItem<ExtendedTreeItemProps> | null>(null);
  const [isNameEditing, setIsNameEditing] = useState<boolean>(false);
  const [renameSearch, setRenameSearch] = useState('');
  const [renameSearchError, setRenameSearchError] = useState<boolean>(false);
  const [savedGroupDetails, setSavedGroupDetails] =
    useState<ISavedGroupDetails>(initialSavedGoupDetails);
  const [isEditNotes, setIsEditNotes] = useState<boolean>(false);
  const [updatedNotes, setUpdatedNotes] = useState<string>('');
  const [isLoadingEditNotes, setIsLoadingEditNotes] = useState<boolean>(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedGroupName, setSelectedGroupName] = useState<string>('');
  const [relationalGroupDetails, setRelationalGroupDetails] = useState<
    string[]
  >([]);

  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const isMoveCompleted = useRef<boolean>(false);
  const {showToast} = useToast();
  const abortControllers = useRef<Map<string, AbortController>>(new Map());

  const addGroupCheckStatus = useSelector(
    (state: RootState) => state.ecaAdhocSearch.addGroupCheck
  );
  const manageSavedSearch = useSelector(
    (state: RootState) => state.ecaAdhocSearch.manageSavedSearch
  );
  const relationalGroupsList = useSelector(
    (state: RootState) => state.ecaAdhocSearch.relationalGroupsList
  );
  const {showSetScopeLoader} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );
  const dispatch = useDispatch();
  const [popupState, setPopupState] = useState({
    open: false,
    type: '',
    id: '',
    label: '',
    path: '',
  });

  const popupConfig: Record<string, PopupConfig> = {
    cantDeleteGroup: {
      title: t(
        'eca.savedSearchManagement.cantDeleteGroupWarningModal.popupTitle'
      ),
      heading: t(
        'eca.savedSearchManagement.cantDeleteGroupWarningModal.popupHeading'
      ),
      content: t(
        'eca.savedSearchManagement.cantDeleteGroupWarningModal.popupContent'
      ),
      buttonCancel: t(
        'eca.savedSearchManagement.cantDeleteGroupWarningModal.buttonCancel'
      ),
      buttonYes: t(
        'eca.savedSearchManagement.cantDeleteGroupWarningModal.buttonYes'
      ),
      showCancel: false,
    },
    deleteGroup: {
      title: t('eca.savedSearchManagement.deleteGroupWarningModal.popupTitle'),
      heading: t(
        'eca.savedSearchManagement.deleteGroupWarningModal.popupHeading'
      ),
      content: t(
        'eca.savedSearchManagement.deleteGroupWarningModal.popupContent'
      ),
      buttonCancel: t(
        'eca.savedSearchManagement.deleteGroupWarningModal.buttonCancel'
      ),
      buttonYes: t(
        'eca.savedSearchManagement.deleteGroupWarningModal.buttonYes'
      ),
      showCancel: true,
    },
    cantDeleteSavedSearch: {
      title: t(
        'eca.savedSearchManagement.cantDeleteSavedSearchWarningModal.popupTitle'
      ),
      heading: t(
        'eca.savedSearchManagement.cantDeleteSavedSearchWarningModal.popupHeading'
      ),
      content: t(
        'eca.savedSearchManagement.cantDeleteSavedSearchWarningModal.popupContent'
      ),
      buttonCancel: t(
        'eca.savedSearchManagement.cantDeleteSavedSearchWarningModal.buttonCancel'
      ),
      buttonYes: t(
        'eca.savedSearchManagement.cantDeleteSavedSearchWarningModal.buttonYes'
      ),
      showCancel: false,
    },
    deleteSavedSearch: {
      title: t(
        'eca.savedSearchManagement.deleteSavedSearchWarningModal.popupTitle'
      ),
      heading: t(
        'eca.savedSearchManagement.deleteSavedSearchWarningModal.popupHeading'
      ),
      content: t(
        'eca.savedSearchManagement.deleteSavedSearchWarningModal.popupContent'
      ),
      buttonCancel: t(
        'eca.savedSearchManagement.deleteSavedSearchWarningModal.buttonCancel'
      ),
      buttonYes: t(
        'eca.savedSearchManagement.deleteSavedSearchWarningModal.buttonYes'
      ),
      showCancel: true,
    },
  };

  useEffect(() => {
    setLoading(true);
    fetchSavedSearchesTemplateGroupsRoot();
    if (manageSaved) renderSelectedSearchDetail?.(InitialContent());
    return () => {
      eventSourceRef.current?.close();
    };
  }, []);

  const prevSearch = useRef<boolean>(false);
  useEffect(() => {
    if (prevSearch.current) {
      setLoading(true);
      setSelectedNode(null);
      onPickSavedSearch?.(null);
      if (manageSaved) {
        renderSelectedSearchDetail?.(InitialContent());
      }
      fetchSavedSearchesTemplateGroupsRoot(
        searchKeyword ? {searchTerm: searchKeyword} : {}
      );
    }
    prevSearch.current = true;
  }, [searchKeyword]);

  useEffect(() => {
    if (selectedSearch?.id) {
      fetchSavedSearchDetails(selectedSearch.id)
        .then(() => {
          onPickSavedSearch?.({
            id: selectedSearch.id,
            label: savedSearchedDetails.name,
            ecaSearchId: savedSearchedDetails.ecaSearchId,
            directory_structure_ids: currentExpandedIds.join('/'),
          });
        })
        .catch(noop);
    }
  }, []);

  useEffect(() => {
    if (savedSearchedDetails !== initialSavedSearchDetails && handleSearchId) {
      const getSavedSearchId = () => {
        switch (flowType) {
          case Flow_Type.STR:
            return savedSearchedDetails.id;
          case Flow_Type.OTHER:
            return savedSearchedDetails.ecaSearchId;
          default:
            return savedSearchedDetails.ecaSearchId;
        }
      };
      onPickSavedSearch?.({
        id: getSavedSearchId(),
        label: savedSearchedDetails.name,
        ecaSearchId: savedSearchedDetails.ecaSearchId,
        directory_structure_ids: currentExpandedIds.join('/'),
      });
    }
    onFetchSavedSearch?.(savedSearchedDetails);
  }, [savedSearchedDetails]);

  useEffect(() => {
    if (!enableSearch) setSearchKeyword(searchInput ?? '');
    if (manageSaved && searchInput === '') {
      setSelectedNode(null);
      renderSelectedSearchDetail?.(InitialContent());
    }
  }, [searchInput, enableSearch]);

  useEffect(() => {
    if (
      !enableFlayout &&
      renderSelectedSearchDetail &&
      selectedNode &&
      selectedNode.fileType !== 'folder'
    ) {
      renderSelectedSearchDetail(renderLoadFlyoutContent());
    }
  }, [
    savedSearchedDetails,
    selectedNode,
    enableFlayout,
    renderSelectedSearchDetail,
    loadingLastDocCount,
    loading,
  ]);

  useEffect(() => {
    if (selectedSearch) {
      setSelectedNode(selectedSearch);
    }
  }, [selectedSearch]);

  useEffect(() => {
    if (savedGroupDetails !== initialSavedGoupDetails && manageSaved) {
      renderSelectedSearchDetail?.(
        renderFolderContent(
          selectedGroupId,
          selectedGroupName,
          savedGroupDetails
        )
      );
    }
  }, [savedGroupDetails]);

  const savedSearchesTemplateRequestParam = `?clientId=${clientId}`;

  const setHasAnyTemplate = async () => {
    const result = await getSavedSearchesTemplateGroupsRoot(clientId);
    setHasTemplate(result?.data?.data?.length ? true : false);
  };

  /**
   * Fetch saved search Root API
   */
  const fetchSavedSearchesTemplateGroupsRoot = async (
    params: Record<string, unknown> = {},
    ignoreExpandedIds = false
  ) => {
    if (clientId) {
      try {
        const result = await getSavedSearchesTemplateGroupsRoot(
          clientId,
          params
        );
        const {data: savedSearchData} = result.data;

        if (!isEmpty(savedSearchData)) {
          const resData: SavedSearchRes = getSavedSearchResFromEntity(
            savedSearchData?.[0] ?? {}
          );

          const mappedArray = [resData].map(
            (item: SavedSearchRes, index: number) =>
              getTreeObjectFromSavedSearch(item, index, item?.groupId ?? '')
          );

          if (!isEmpty(searchKeyword)) {
            setSelectedNode(selectedSearch);
            setCurrentExpandedIds(getExpandableIds(mappedArray));
          } else if (!ignoreExpandedIds) {
            setSelectedNode(selectedSearch);
            setCurrentExpandedIds(expandedIds ?? ['']);
          }

          if (selectedNodeToMove && isMoveCompleted.current) {
            const updatedNode = findTreeNodeById(
              mappedArray,
              selectedNodeToMove.id
            );
            if (updatedNode) {
              isMoveCompleted.current = false;
              setSelectedNode({...updatedNode});
              setCurrentExpandedIds(
                updatedNode?.directory_structure_ids?.split('/') ?? []
              );
            }
          }

          setSaveSearchedData(mappedArray);
        } else {
          setSaveSearchedData([]);
        }
        setTimeout(() => {
          setLoading(false);
        }, 1500);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
        setLoading(false);
      }
    }
  };

  const fetchUpdatedSavedSearchesTemplateGroupsRoot = async () => {
    if (clientId) {
      try {
        const result = await getSavedSearchesTemplateGroupsRoot(clientId, {
          ...(searchKeyword ? {searchTerm: searchKeyword} : {}),
        });
        const {data: savedSearchData} = result.data;
        let mappedArray: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];
        if (!isEmpty(savedSearchData)) {
          const resData: SavedSearchRes = getSavedSearchResFromEntity(
            savedSearchData?.[0] ?? {}
          );

          mappedArray = [resData].map((item: SavedSearchRes, index: number) =>
            getTreeObjectFromSavedSearch(item, index, item?.groupId ?? '')
          );
        }

        return mappedArray;
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
      }
    }
  };

  const fetchTemplateGroupDetails = async (id: string) => {
    if (clientId && id) {
      try {
        const result = await getTemplateGroupDetails(
          clientId,
          extractSavedSearchId(id)
        );
        setSavedGroupDetails(result?.data?.data);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
      }
    }
  };

  const getSelectedRelationshipGroupTextArray = async (
    relationalGroups: number[]
  ) => {
    if (relationalGroupsList && relationalGroups)
      return relationalGroupsList
        .filter((item: RelationalGroup) => relationalGroups.includes(item.id))
        .map((item) => item.name);
    return [];
  };

  /**
   * Get details of selected saved search item
   */
  const fetchSavedSearchDetails = async (id: string, docCount = true) => {
    if (docCount) {
      setLoadingFlyout(true);
    }
    if (clientId) {
      try {
        const result = await getTemplateSavedSearchesDetails(
          clientId,
          extractSavedSearchId(id)
        );
        const {data} = result.data;
        data.query = data.query && unwrapQueryText(data.query);
        if (handleSearchId) {
          dispatch(setSavedSearchLastRun(!checkSavedSearchLastRun(data)));
        }
        setSavedSearchedDetails(data);
        const relationalGrpDetails =
          await getSelectedRelationshipGroupTextArray(data.relationalGroups);
        setRelationalGroupDetails(relationalGrpDetails);
        setLoadingLastDocCount(false);
      } catch (error: unknown) {
        const err = error as AxiosError;
        showToast(err.message, 'error', undefined, 'center');
        setLoadingLastDocCount(false);
        handleClose();
      } finally {
        setLoadingFlyout(false);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      abortControllers.current.forEach((controller) => {
        controller.abort();
      });
      abortControllers.current.clear();
    };
  }, [savedSearchedDetails.ecaSearchId]);

  const eventSourceRef = useRef<EventSource | null>(null);
  // commented because getSearchIdSse is not in use
  // const getSearchIdSse = useCallback(
  //   (requestId: string) => {
  //     if (requestId && requestId != '') {
  //       const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.SEARCH_SERVICE}`;
  //       eventSourceRef.current = new EventSource(SSE_URL);
  //       eventSourceRef.current.onmessage = (event) => {
  //         if (eventSourceRef.current) {
  //           eventSourceRef.current.close();
  //         }
  //         const responseData = JSON.parse(event.data);
  //         if (responseData.status.toLowerCase() == 'completed') {
  //           fetchSavedSearchDetails(savedSearchedDetails.id, false);
  //         } else {
  //           showToast(t('common.somethingWrong'), 'error', undefined, 'center');
  //           setLoadingLastDocCount(false);
  //         }

  //         if (eventSourceRef.current) {
  //           eventSourceRef.current.close();
  //         }
  //       };

  //       eventSourceRef.current.onerror = () => {
  //         setLoadingLastDocCount(false);
  //         showToast(t('common.somethingWrong'), 'error', undefined, 'center');
  //         if (eventSourceRef.current) {
  //           eventSourceRef.current.close(); // Close previous connection if it exists
  //         }
  //       };
  //     }
  //   },
  //   [savedSearchedDetails.ecaSearchId]
  // );

  const handleNameEditClick = (name: string) => {
    setRenameSearchError(false);
    setRenameSearch(name);
    setIsNameEditing(true);
  };

  const renameSearchHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setRenameSearchError(!isValidName(value));
    setRenameSearch(value);
  };

  useEffect(() => {
    if (isDeleteSuccessful) {
      handleClose();
      setLoading(true);
      setSaveSearchedData([]);
      fetchSavedSearchesTemplateGroupsRoot(
        searchKeyword ? {searchTerm: searchKeyword} : {}
      );
      setHasAnyTemplate();
    }
  }, [isDeleteSuccessful]);

  useEffect(() => {
    if (!isCreatingRootFolder) fetchSavedSearchesTemplateGroupsRoot();
  }, [isCreatingRootFolder]);

  useEffect(() => {
    setHasAnyTemplate();
  }, [isCreatingRootFolder]);

  const handleSaveNameClick = async () => {
    setIsSaving(true);
    const syntaxQuery = wrapQueryBeforeSymbol(
      savedSearchedDetails?.query +
        createQueryUsingScopeObj(savedSearchedDetails?.scope)
    );
    const reqBody: SavedSearchDetails = {
      ...savedSearchedDetails,
      name: renameSearch,
      query: syntaxQuery,
      noScopeDtSyntaxQuery: wrapQueryBeforeSymbol(savedSearchedDetails?.query),
      dtSyntaxQuery: syntaxQuery,
      relationalGroupFieldList: savedSearchedDetails?.relationalGroups,
      relationalGroups: undefined,
    };
    try {
      const results = await updateTemplateSavedSearchesDetails(
        savedSearchesTemplateRequestParam,
        savedSearchedDetails.id,
        reqBody
      );
      if (results) {
        handleClose();
        setLoading(true);
        setSaveSearchedData([]);
        fetchSavedSearchesTemplateGroupsRoot(
          searchKeyword ? {searchTerm: searchKeyword} : {}
        );
      }
    } catch (error: unknown) {
      const e = error as AxiosError;
      showToast(e.message, 'error', undefined, 'center');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveNotes = async (id: string) => {
    setIsLoadingEditNotes(true);
    const formattedUpdatedNotes = updatedNotes
      .split('\n')
      .filter((i) => i)
      .join('\n');
    const syntaxQuery = wrapQueryBeforeSymbol(
      savedSearchedDetails?.query +
        createQueryUsingScopeObj(savedSearchedDetails?.scope)
    );
    const reqBody: SavedSearchDetails = {
      ...savedSearchedDetails,
      notes: formattedUpdatedNotes,
      query: syntaxQuery,
      noScopeDtSyntaxQuery: wrapQueryBeforeSymbol(savedSearchedDetails?.query),
      dtSyntaxQuery: syntaxQuery,
      relationalGroupFieldList: savedSearchedDetails?.relationalGroups,
      relationalGroups: undefined,
    };
    try {
      const results = await updateTemplateSavedSearchesDetails(
        savedSearchesTemplateRequestParam,
        id,
        reqBody
      );
      if (results) {
        setIsEditNotes(false);
        fetchSavedSearchDetails(savedSearchedDetails.id);
        setIsLoadingEditNotes(false);
      }
    } catch (error: unknown) {
      const e = error as AxiosError;
      showToast(e.message, 'error', undefined, 'center');
      setIsLoadingEditNotes(false);
    }
  };

  const renderDateFieldWithTooltip = (
    value?: string,
    className = 'body4_size location-label'
  ) => {
    return (
      <AurLightTooltip title={value ? t('common.yearMonthDate') : ''}>
        <Typography className={`typo-text ${className}`}>
          {value && getUTCDateTimeForDisplay(value)}
        </Typography>
      </AurLightTooltip>
    );
  };

  /**
   * Render flyout
   * @returns
   */
  const InitialContent = () => {
    return (
      <Box className="flyout-container-saved-search">
        <Typography className="typo-text subhead3_size folder-title">
          {t('eca.flyout.savedSearch.summary')}
        </Typography>
      </Box>
    );
  };
  const renderFolderContent = (
    id: string,
    name: string,
    savedGroupDetail: ISavedGroupDetails
  ) => {
    const {createdBy, createdTime, updatedBy, updatedTime} = savedGroupDetail;

    return (
      <Box className="flyout-container-saved-search">
        <Typography className="typo-text subhead3_size folder-title">
          {name || ''}
        </Typography>
        <Box className="copy-id">
          <Typography className="typo-text body4_size location-label">
            {t('eca.flyout.id')}:
          </Typography>
          <Typography className="typo-text body4_size location-label">
            {extractSavedSearchId(id)}
          </Typography>
        </Box>
        <Box className="details-container">
          <Box className="copy-id folder-details-row">
            <Typography className="typo-text label2_size label-id">
              {t('eca.flyout.savedSearch.createdOn')}
            </Typography>
            {renderDateFieldWithTooltip(createdTime)}
          </Box>
          <Box className="copy-id folder-details-row">
            <Typography className="typo-text label2_size label-id">
              {t('eca.flyout.savedSearch.createdBy')}
            </Typography>
            <Typography className="typo-text body4_size location-label">
              {createdBy != '' ? createdBy : '--'}
            </Typography>
          </Box>
          <Box className="copy-id folder-details-row">
            <Typography className="typo-text label2_size label-id">
              {t('eca.flyout.savedSearch.modifiedOn')}
            </Typography>
            {renderDateFieldWithTooltip(updatedTime)}
          </Box>
          <Box className="copy-id folder-details-row">
            <Typography className="typo-text label2_size label-id">
              {t('eca.flyout.savedSearch.modifiedBy')}
            </Typography>
            <Typography className="typo-text body4_size location-label">
              {updatedBy != '' ? updatedBy : '--'}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };
  const renderLoadFlyoutContent = () => {
    const {
      id = '',
      name = '',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      lastRunDetail: {lastRunTime = ''} = {
        lastRunUserName: '',
        lastRunTime: '',
        lastRunUserId: '',
      },
      createdDetail: {createdUserName = '', createdTime = ''} = {
        createdUserName: '',
        createdTime: '',
        createdUserId: '',
      },
      updatedDetail: {updatedUserName = '', updatedTime = ''} = {
        updatedUserName: '',
        updatedTime: '',
      },
      query = '',
      columnOrder = [''],
      sortOrder = [
        {
          column: '',
          direction: '',
        },
      ],
      notes = '',
      indexName: indexNameValue,
      isDefault = false,
      ecaSearchId,
    } = savedSearchedDetails;

    const handleEditNotes = () => {
      setIsEditNotes(true);
      setUpdatedNotes(notes);
    };

    return (
      <div id="eca-flyout-container">
        <Box className="flyout-container-saved-search">
          {loadingFlyout ? (
            <AurLoadingIndicator
              show={true}
              size={48}
              className="loader-save-search"
            />
          ) : (
            <>
              <Box className="flyout-header">
                <Box className="name-container">
                  {!isNameEditing ? (
                    <Typography
                      className={`typo-text ${openForPickSavedSearch ? 'subhead3_size custom-color' : 'headline2_size'} custom-title-text`}
                    >
                      {name}
                    </Typography>
                  ) : (
                    <AurTextField
                      error={renameSearchError}
                      helperText={
                        renameSearchError
                          ? t('eca.flyout.savedSearch.nameError')
                          : ''
                      }
                      data-testid="rename-save-search"
                      name={t('eca.flyout.savedSearch.name')}
                      onChange={renameSearchHandler}
                      value={renameSearch}
                      fullWidth
                      label={t('eca.flyout.savedSearch.name')}
                      variant="filled"
                      className={`aur-textfield rename-text-field`}
                      ariaLabel={t('eca.flyout.savedSearch.name')}
                      inputProps={{
                        maxLength: 50,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {renameSearchError ? (
                              <InfoIcon />
                            ) : (
                              <>
                                <AurButton
                                  classNames="cancel-button"
                                  onClick={() => setIsNameEditing(false)}
                                  type="exit"
                                  size="Small"
                                  dataTestId="cancel-button"
                                  label={t('eca.flyout.savedSearch.cancel')}
                                  minWidth={54}
                                />
                                <AurLoadingButton
                                  label={t('eca.flyout.savedSearch.save')}
                                  loading={isSaving}
                                  size="Small"
                                  dataTestId="rename-name-button"
                                  onClick={handleSaveNameClick}
                                  minWidth={54}
                                  classNames="save-button"
                                  disabled={isSaving}
                                />
                              </>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  {!openForPickSavedSearch &&
                    !manageSaved &&
                    !isNameEditing &&
                    !isDefault && (
                      <AurIconButton
                        size="small"
                        onClick={() => handleNameEditClick(name)}
                        shape
                        icon={<AurIcon icon={IconNames.Edit} />}
                        dataTestId="edit-name-button"
                        ariaLabel={t('eca.flyout.savedSearch.editSearchName')}
                      />
                    )}
                </Box>
                {openForPickSavedSearch && !manageSaved && (
                  <Box marginBottom={2} display="flex" gap={0.25}>
                    <Typography className="typo-text label2_size label-id-v2">
                      {t('eca.flyout.id')}:
                    </Typography>
                    <Typography className={`typo-text label2_size label-id-v2`}>
                      {ecaSearchId}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box className="cell-container">
                <Box className="row-style">
                  <Box className="label-text">
                    <Typography className="typo-text body4_size custom-color-cell">
                      {t('eca.flyout.savedSearch.created')}:
                    </Typography>
                  </Box>
                  <Box
                    className={`value-text ${openForPickSavedSearch && 'value-text-column'}`}
                  >
                    <Typography className="typo-text body4_size custom-color-cell user-name-value">
                      {createdUserName}
                    </Typography>

                    <Box
                      className={`time-stamp-value ${openForPickSavedSearch && 'time-stamp-value-v2'}`}
                    >
                      {renderDateFieldWithTooltip(
                        createdTime,
                        'body4_size custom-color-cell'
                      )}
                    </Box>
                  </Box>
                </Box>
                <Divider orientation="horizontal" flexItem />
                <Box className="row-style">
                  <Box className="label-text">
                    <Typography className="typo-text body4_size custom-color-cell">
                      {t('eca.flyout.savedSearch.modified')}:
                    </Typography>
                  </Box>
                  <Box
                    className={`value-text ${openForPickSavedSearch && 'value-text-column'}`}
                  >
                    <Typography className="typo-text body4_size custom-color-cell user-name-value">
                      {updatedUserName}
                    </Typography>

                    <Box
                      className={`time-stamp-value ${openForPickSavedSearch && 'time-stamp-value-v2'}`}
                    >
                      {renderDateFieldWithTooltip(
                        updatedTime,
                        'body4_size custom-color-cell'
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="parameters-section template-parameters-section">
                <Box className="parameters-heading">
                  <Typography className="typo-text subhead4_size custom-color">
                    {t('eca.flyout.savedSearch.parameters')}
                  </Typography>
                </Box>

                <Box className="parameters-item">
                  <Box className="content-single-row">
                    <Typography className="typo-text label2_size">
                      {indexNameLabel}:
                    </Typography>
                    <Typography className="typo-text body3_size parameter-value">
                      {indexNameValue}
                    </Typography>
                  </Box>
                </Box>

                <Divider orientation="horizontal" flexItem />
                <Box className="parameters-item">
                  <Box className="content-single-row">
                    <Typography className="typo-text label2_size">
                      {t('eca.flyout.savedSearch.scope')}:
                    </Typography>
                    <Typography className="typo-text body3_size parameter-value">
                      {savedSearchedDetails?.scope?.scopeName
                        ? getLabelFromScopeQuery(
                            savedSearchedDetails?.scope?.scopeName
                          )
                        : t('common.none')}
                    </Typography>
                  </Box>
                </Box>
                <Divider orientation="horizontal" flexItem />
                <Box className="parameters-item">
                  <Box
                    className={`${relationalGroupDetails.length ? 'relational-grp-details' : 'content-single-row'}`}
                  >
                    <Typography className="typo-text label2_size">
                      {t('eca.flyout.savedSearch.relationalGroup')}:
                    </Typography>
                    <Typography className="typo-text body3_size parameter-value relational-grp-labels">
                      {relationalGroupDetails.length ? (
                        <>
                          {relationalGroupDetails?.map((i) => (
                            <Typography
                              className="typo-text body3_size custom-color-cell"
                              key={i}
                            >
                              {i}
                            </Typography>
                          ))}
                        </>
                      ) : (
                        t('common.none')
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Divider orientation="horizontal" flexItem />
                <Box className="accordian-item">
                  <Accordion defaultExpanded>
                    <AccordionSummary
                      expandIcon={<GridExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                      aria-label={`${t('eca.flyout.savedSearch.searchQuery')} ${t('eca.flyout.savedSearch.syntax')}`}
                    >
                      <Typography
                        component="span"
                        className="typo-text label2_size"
                      >
                        {t('eca.flyout.savedSearch.syntax')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography className="typo-text body3_size parameter-value">
                        {query}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
                <Divider orientation="horizontal" flexItem />
                <Box className="accordian-item">
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<GridExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                    >
                      <Typography
                        component="span"
                        className="typo-text label2_size"
                      >
                        {t('eca.flyout.savedSearch.columnsSortOrder')}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography className="typo-text label2_size">
                        {t('eca.flyout.savedSearch.columns')}:
                      </Typography>
                      {columnOrder.map((i) => (
                        <Typography
                          key={i}
                          className="typo-text body3_size parameter-value"
                        >
                          {i}
                        </Typography>
                      ))}
                      <p className="typo-text label2_size"></p>
                      <Typography className="typo-text label2_size">
                        {t('eca.flyout.savedSearch.sortOrder')}:
                      </Typography>
                      {sortOrder.map((i) => (
                        <Typography
                          key={i.column}
                          className="typo-text body3_size parameter-value"
                        >
                          <span>{i.column}: </span>
                          <span>{i.direction.toUpperCase()}</span>
                        </Typography>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Box>

              {showNotes && (!isDefault || (isDefault && notes)) ? (
                <>
                  {isEditNotes ? (
                    <Box className="edit-note-content">
                      <Typography className="typo-text subhead4_size custom-color">
                        {t('eca.flyout.savedSearch.notes')}
                      </Typography>
                      <AurTextField
                        variant="filled"
                        maxRows={5}
                        fullWidth
                        multiline
                        className="aur-textfield custom-text-field"
                        inputProps={{
                          maxLength: MAX_CHAR,
                        }}
                        value={updatedNotes}
                        onChange={(e) => {
                          setUpdatedNotes(e.target.value);
                        }}
                        ariaLabel={t('eca.flyout.savedSearch.notes')}
                        sx={{
                          '& .MuiInputBase-root': {
                            resize: 'vertical',
                            overflow: 'auto',
                            display: 'flex',
                            alignItems: 'flex-start',
                          },
                        }}
                        data-testid="edit-notes-input"
                        helperText={t('eca.flyout.savedSearch.noteHelpText')}
                      />
                      <Box className="edit-note-btn-container">
                        <AurButton
                          classNames="custom-btn"
                          type="exit"
                          label={t('buttons.cancel')}
                          size="Small"
                          title={t('buttons.cancel')}
                          onClick={() => {
                            setIsEditNotes(false);
                            setUpdatedNotes('');
                          }}
                          disabled={isLoadingEditNotes}
                        />
                        <AurButton
                          classNames="custom-btn"
                          label={t('buttons.saveChanges')}
                          type="continue"
                          size="Small"
                          dataTestId="edit-note-save"
                          title={t('buttons.saveChanges')}
                          onClick={() => {
                            handleSaveNotes(id);
                          }}
                          disabled={isLoadingEditNotes}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box className="note-section template-note-section">
                      <Box className="note-content">
                        <Typography className="typo-text subhead4_size custom-color">
                          {t('eca.flyout.savedSearch.notes')}
                        </Typography>
                        <Box>
                          {notes.split('\n').map((i: string, index: number) => (
                            <Typography
                              key={index + i}
                              className="typo-text body3_size parameter-value"
                            >
                              {i}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                      {!openForPickSavedSearch &&
                        !isDefault &&
                        !manageSaved && (
                          <AurIconButton
                            size="small"
                            shape
                            dataTestId="edit-notes-button"
                            icon={<AurIcon icon={IconNames.Edit} />}
                            onClick={handleEditNotes}
                            ariaLabel={t('eca.flyout.savedSearch.editNotes')}
                          />
                        )}
                    </Box>
                  )}
                </>
              ) : null}

              <>
                <Divider orientation="horizontal" flexItem />
                <Box className="delete-section template-delete-section">
                  <Box className="delete-content">
                    <Typography className="typo-text body3_size custom-color">
                      {t('savedSearchesTemplate.deleteContent')}
                    </Typography>
                  </Box>
                </Box>
              </>
            </>
          )}
        </Box>
      </div>
    );
  };

  /**
   * Handle selected tree node
   * @param selectedNode
   * @param event
   */
  const handleSelectedItem = (
    selectedNode: TreeViewBaseItem<ExtendedTreeItemProps>,
    event?: React.SyntheticEvent
  ) => {
    setSelectedNodeToMove(selectedNode || '');
    if (selectedNode.fileType !== 'folder') {
      setSelectedNode(selectedNode);
      if (onPickSavedSearch) {
        onPickSavedSearch({
          id: selectedNode.id,
          label: selectedNode.label,
          ecaSearchId: selectedNode.ecaSearchId ?? '',
          directory_structure_ids: selectedNode.directory_structure_ids,
        });
      }
      if (event) {
        fetchSavedSearchDetails(extractSavedSearchId(selectedNode.id));
        if (enableFlayout) {
          setAnchorEl(
            drawerRef?.current ?? (event.currentTarget as HTMLElement)
          );
          setShowFlyout(true);
          setTimeout(() => {
            document.getElementById('eca-flyout-container')?.focus();
          }, 100);
        } else if (renderSelectedSearchDetail) {
          renderSelectedSearchDetail(renderLoadFlyoutContent());
        }
      }
    } else if (
      selectedNode.fileType === 'folder' &&
      renderSelectedSearchDetail &&
      folderDetailsView &&
      !addGroupCheckStatus
    ) {
      fetchTemplateGroupDetails(selectedNode.id);
      setSelectedGroupName(selectedNode.label);
      setSelectedGroupId(selectedNode.id);
    }
  };

  /**
   * Handle close flyout
   */
  const handleClose = () => {
    setShowFlyout(false);
    setSelectedNode(null);
    setIsNameEditing(false);
    setIsEditNotes(false);
    setUpdatedNotes('');
    setSavedSearchedDetails(initialSavedSearchDetails);
    setRelationalGroupDetails([]);
  };

  const handlePopupClose = () => {
    setPopupState({open: false, type: '', id: '', label: '', path: ''});
  };

  const handlePopupOk = () => {
    if (popupState.type === 'deleteGroup') {
      deleteSavedSearchGroupTemplateHandler(
        popupState.id,
        popupState.label,
        popupState.path
      );
    } else if (popupState.type === 'deleteSavedSearch') {
      deleteSavedSearchTemplateHandler(
        popupState.id,
        popupState.label,
        popupState.path
      );
    } else {
      handlePopupClose();
    }
  };

  const handleDeleteFromOverflowMenu = (
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    if (node.fileType === 'folder') {
      if (node.children?.length) {
        setPopupState({
          open: true,
          type: 'cantDeleteGroup',
          id: node.id,
          label: node.label,
          path: node?.directory_structure_ids ?? '',
        });
      } else {
        setPopupState({
          open: true,
          type: 'deleteGroup',
          id: node.id,
          label: node.label,
          path: node?.directory_structure_ids ?? '',
        });
      }
    } else {
      setPopupState({
        open: true,
        type: 'deleteSavedSearch',
        id: node.id,
        label: node.label,
        path: node?.directory_structure_ids ?? '',
      });
    }
  };

  const handleSelect = (
    item: string,
    node: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    switch (item) {
      case t('eca.flyout.savedSearch.move'):
        setIsMoveModalOpen(true);
        break;
      case t('buttons.delete'):
        handleDeleteFromOverflowMenu(node);
        break;
    }
  };
  const handleCloseMoveModal = () => {
    setIsMoveModalOpen(false);
  };

  const onSuccessAction = (label: string, ignoreExpandedIds = false) => {
    showToast(label, 'success', TOAST_TIMEOUT, 'center', 'bottom');
    setLoading(true);
    setSaveSearchedData([]);
    fetchSavedSearchesTemplateGroupsRoot(
      searchKeyword ? {searchTerm: searchKeyword} : {},
      ignoreExpandedIds
    );
  };

  const onActionFailed = (message: string) => {
    showToast(message, 'error', TOAST_TIMEOUT, 'center', 'bottom');
  };
  const deleteSavedSearchGroupTemplateHandler = async (
    id: string,
    label: string,
    path: string
  ) => {
    const groupId = extractSavedSearchId(id);
    try {
      const results = await deleteSavedSearchGroupTemplate(groupId);
      if (results.status === 200) {
        const updatePathCompArray = path.split('/');
        updatePathCompArray.length > 1 && updatePathCompArray.pop();

        const parentID =
          updatePathCompArray.length > 1
            ? updatePathCompArray.at(-1)
            : (updatePathCompArray?.[0] ?? '');
        setSelectedNode(
          !isEmpty(parentID)
            ? ({id: parentID} as TreeViewBaseItem<ExtendedTreeItemProps>)
            : null
        );
        deleteSuccessAction(label, updatePathCompArray.join('/'));
        renderSelectedSearchDetail?.(InitialContent());
      }
    } catch (e) {
      handlePopupClose();
      onActionFailed(t('eca.savedSearchManagement.searchGroupDeletedError'));
    }
  };

  const deleteSavedSearchTemplateHandler = async (
    id: string,
    label: string,
    path: string
  ) => {
    const savedSearchId = extractSavedSearchId(id);
    try {
      const results = await deleteSavedSearchTemplateById(savedSearchId);
      if (results.status === 200) {
        const updatePathCompArray = path.split('/');
        const parentID =
          updatePathCompArray.length > 1
            ? updatePathCompArray.at(-1)
            : (updatePathCompArray?.[0] ?? '');

        setSelectedNode(
          !isEmpty(parentID)
            ? ({id: parentID} as TreeViewBaseItem<ExtendedTreeItemProps>)
            : null
        );
        deleteSuccessAction(label, updatePathCompArray.join('/'));
        renderSelectedSearchDetail?.(InitialContent());
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      if (err?.status === ERROR_STATUS_CODE.STATUS_409) {
        setPopupState({
          open: true,
          type: 'cantDeleteSavedSearch',
          id: '',
          label: '',
          path: '',
        });
      } else {
        handlePopupClose();
        onActionFailed(t('eca.savedSearchManagement.savedSearchDeletedError'));
      }
    }
  };

  const deleteSuccessAction = (label: string, path: string) => {
    handlePopupClose();
    syncExpandedIds(path.split('/'));
    onSuccessAction(
      t('eca.savedSearchManagement.groupOrSavedSearchDeleted', {
        deletedGroupOrSavedSearchName: label,
      }),
      true
    );
  };

  const renderSearchBox = () => {
    return (
      <Box className="search-container" data-testid="search-container">
        <AurSearchBox
          label={t('common.filterList')}
          minCharForSearch={MIN_SEARCH_CHAR}
          onSearch={(keyword: string) => {
            setSearchKeyword(keyword.trim());
          }}
          classNameProps="filter-text-field"
        />
      </Box>
    );
  };

  const syncExpandedIds = (expandedIds: string[]) => {
    setExpandedNodes(expandedIds);
    setCurrentExpandedIds(expandedIds);
  };

  const onMoveComplete = (
    preExpandedIds: string[],
    selectedNodeToMove: TreeViewBaseItem<ExtendedTreeItemProps>,
    type: string
  ) => {
    syncExpandedIds(preExpandedIds);
    renderSelectedSearchDetail?.(InitialContent());
    isMoveCompleted.current = true;
    if (type === 'SavedSearchGroup') {
      fetchTemplateGroupDetails(extractSavedSearchId(selectedNodeToMove.id));
    } else {
      fetchSavedSearchDetails(extractSavedSearchId(selectedNodeToMove.id));
    }
  };

  const getDefaultIds = useCallback(() => {
    const ids = [] as string[];
    const findMatchingIds = (
      node: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ) => {
      node.forEach((item) => {
        if (expandedNodes.includes(item.id)) ids.push(item.id);
        if (item?.children && item.children.length > 0) {
          findMatchingIds(item.children);
        }
      });
    };
    if (treeData) findMatchingIds(treeData);
    return ids?.filter((id) => !!id);
  }, [expandedNodes, JSON.stringify(treeData)]);

  return (
    <>
      {enableSearch ? renderSearchBox() : null}
      <Box
        className={`drawer-treeview-content custom-treeview ${saveSearchedData.length === 0 && 'zero-saved-search'}`}
        data-testid="location-treeview-content"
      >
        {loading ? (
          <AurLoadingIndicator show={true} />
        ) : (
          <>
            {!isCreatingRootFolder && !hasTemplate ? (
              <EmptyTemplateBackground
                data-testid="start-new-group-btn"
                startNewGroupHandler={() => {
                  setIsCreatingRootFolder(true);
                }}
              />
            ) : (
              <AurLocationTreeView
                itemsList={saveSearchedData}
                fetchUpdatedRoot={fetchUpdatedSavedSearchesTemplateGroupsRoot}
                handleSelectedItem={handleSelectedItem}
                selectedItem={selectedNode?.id || ''}
                defaultExpandedIds={currentExpandedIds}
                allowGlobalSelect={true}
                showActions={showActions}
                handleSelect={handleSelect}
                manageSavedSearch={manageSavedSearch}
                manageSearchRename={manageSearchRename}
                savedSearchedDetails={{
                  ...savedSearchedDetails,
                  query: wrapQueryBeforeSymbol(
                    savedSearchedDetails?.query +
                      createQueryUsingScopeObj(savedSearchedDetails?.scope)
                  ),
                  noScopeDtSyntaxQuery: wrapQueryBeforeSymbol(
                    savedSearchedDetails?.query
                  ),
                  dtSyntaxQuery: wrapQueryBeforeSymbol(
                    savedSearchedDetails?.query +
                      createQueryUsingScopeObj(savedSearchedDetails?.scope)
                  ),
                  relationalGroupFieldList:
                    savedSearchedDetails?.relationalGroups,
                  relationalGroups: undefined,
                }}
                setExpandedFolders={setExpandedNodes}
                preSelectedId={selectedNode?.id}
                updateTreeData={setTreeData}
                showTooltip={true}
                showTooltipAsNodeName={true}
                isTemplate={true}
                allowRootRename
                isCreatingRootFolder={isCreatingRootFolder}
                templateOperations={{
                  templeteClientId: clientId,
                  setIsCreatingRootFolder: setIsCreatingRootFolder,
                  addNewGroup: addNewGroup,
                  updateGroupTemplate: updateSavedSearchGroupTemplate,
                  updateSavedSearchTemplate: updateSavedSearchTemplate,
                }}
              />
            )}
          </>
        )}
      </Box>
      <ECAFlyout
        data-testid="saved-search-flyout"
        showFlyout={showFlyout}
        anchorEl={anchorEl}
        onClose={handleClose}
        content={renderLoadFlyoutContent()}
        className="custom-popper-saved-search"
      />
      {popupState.open && (
        <AurPopup
          className={`aur-unsaved-changes-popup ${!popupConfig[popupState.type]?.showCancel && 'single-button-popup'}`}
          open={popupState.open}
          onOk={handlePopupOk}
          onCancel={handlePopupClose}
          onClose={handlePopupClose}
          popupTitle={popupConfig[popupState.type]?.title}
          popupHeading={popupConfig[popupState.type]?.heading}
          popupContent={popupConfig[popupState.type]?.content}
          showPopupHeading={true}
          showPopupTitle={true}
          showCloseIcon={true}
          showCancelOption={popupConfig[popupState.type]?.showCancel}
          labelActionCancel={popupConfig[popupState.type]?.buttonCancel}
          labelActionOk={popupConfig[popupState.type]?.buttonYes}
          cancelButtonTitle=""
          dataTestId="saved-search-popup"
        />
      )}
      {showSetScopeLoader && (
        <Box className="left-scope-loader">
          <AurLoadingIndicator
            show={showSetScopeLoader}
            className="loader-icon"
          />
        </Box>
      )}
      {isMoveModalOpen && (
        <MoveSavedSearchGroupPopup
          data-testid="move-modal"
          isOpen={isMoveModalOpen}
          onClose={handleCloseMoveModal}
          itemsList={saveSearchedData}
          defaultExpandedIds={getDefaultIds()}
          selectedNodeToMove={selectedNodeToMove}
          savedSearchDetails={{
            ...savedSearchedDetails,
            query: wrapQueryBeforeSymbol(
              savedSearchedDetails?.query +
                createQueryUsingScopeObj(savedSearchedDetails?.scope)
            ),
            noScopeDtSyntaxQuery: wrapQueryBeforeSymbol(
              savedSearchedDetails?.query
            ),
            dtSyntaxQuery: wrapQueryBeforeSymbol(
              savedSearchedDetails?.query +
                createQueryUsingScopeObj(savedSearchedDetails?.scope)
            ),
            relationalGroupFieldList: savedSearchedDetails?.relationalGroups,
            relationalGroups: undefined,
          }}
          onSuccessAction={onSuccessAction}
          onActionFailed={onActionFailed}
          onMoveComplete={onMoveComplete}
          isTemplate={true}
          templateOps={{
            onMoveGroupTemplate: updateSavedSearchGroupTemplate,
            onMoveSearchTemplate: updateSavedSearchTemplate,
          }}
        />
      )}
    </>
  );
};

export default SavedSearchesTemplatePanel;
